import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { ContactForm } from '../components/contact/ContactForm';
import { directContacts, InquiryType, inquiryReasons } from '../data/contact';
import { easeOutStrong, fadeUp } from '../utils/motion';
export function ContactPage() {
  const [presetInquiry, setPresetInquiry] = useState<InquiryType>('');
  const chooseReason = (value: InquiryType) => {
    setPresetInquiry(value);
    document.getElementById('contact-form')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
    window.setTimeout(() => document.getElementById('firstName')?.focus({
      preventScroll: true
    }), 400);
  };
  return <>
      <section className="bg-surface-bg border-b border-line pt-32 pb-20 lg:pt-44 lg:pb-28" data-id="element-2863" data-mp="ez4ljgup77b8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16" data-id="element-2864" data-mp="1lfgcoxz3roxb">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          ease: easeOutStrong
        }} className="lg:col-span-5" data-id="element-2865" data-mp="1qcyoqx8f2k9j">
            <p className="text-sm font-medium text-teal mb-5" data-id="element-2866" data-mp="mzla4mq7yf87">Contact</p>
            <h1 className="font-heading font-extrabold text-5xl lg:text-6xl text-ink-dark leading-[1.05] tracking-tight mb-8" data-id="element-2867" data-mp="hyxhxxq97sce">
              Get in Touch
            </h1>
            <p className="text-xl text-ink-dark leading-relaxed mb-12" data-id="element-2868" data-mp="81kgy0gq6hhr">
              We’d like to hear from people and organizations interested in
              knowledge infrastructure, research collaboration, Noetivra’s
              interface and modules, human capability frameworks, or
              institutional pilots.
            </p>

            <dl className="border-t border-line" data-id="element-2869" data-mp="3qhd5mb3kdhb5">
              {directContacts.map(contact => <div key={contact.email} className="border-b border-line py-5" data-id="element-2870" data-mp="3lg7cic5zrs13">
                  <dt className="text-sm text-ink-muted mb-1" data-id="element-2871" data-mp="3ginm6dw0ross">{contact.label}</dt>
                  <dd data-id="element-2872" data-mp="36tgws5ritwvz">
                    <a href={`mailto:${contact.email}`} className="font-heading font-bold text-lg text-navy hover:text-navy-light transition-colors duration-150" data-id="element-2873" data-mp="36t8nenulx9pj">
                      {contact.email}
                    </a>
                  </dd>
                </div>)}
              <div className="py-5" data-id="element-2874" data-mp="3pkhukqgwnmdy">
                <dt className="text-sm text-ink-muted mb-1" data-id="element-2875" data-mp="3plj7l0hheiyb">Response time</dt>
                <dd className="text-ink-dark leading-relaxed" data-id="element-2876" data-mp="3uk4a56k7hepx">
                  We typically respond to inquiries within 2–3 business days.
                  For urgent matters, please indicate that in your message.
                </dd>
              </div>
            </dl>
          </motion.div>

          <motion.div id="contact-form" initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          delay: 0.06,
          ease: easeOutStrong
        }} className="lg:col-span-7 scroll-mt-28" data-id="element-2877" data-mp="ce7yolz70f7w">
            <ContactForm presetInquiry={presetInquiry} data-id="element-2878" data-mp="1ww0fd07fmut8" />
          </motion.div>
        </div>
      </section>

      <section className="bg-white py-20 lg:py-28" data-id="element-2879" data-mp="2bkwtnf03142a">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2880" data-mp="2bkdeg61tgtry">
          <motion.div {...fadeUp} className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-12" data-id="element-2881" data-mp="26ljz082yysp5">
            <h2 className="lg:col-span-5 font-heading font-bold text-4xl lg:text-5xl text-ink-dark tracking-tight leading-tight" data-id="element-2882" data-mp="1ivfxq7d9751b">
              Reasons to reach out
            </h2>
            <p className="lg:col-span-7 text-xl text-ink-muted leading-relaxed lg:pt-2" data-id="element-2883" data-mp="1su5chr9us9dc">
              Whether you’re exploring a partnership, requesting a demo, or have
              questions about our research, we’d like to hear from you. Choose a
              topic to start your message.
            </p>
          </motion.div>

          <motion.ul {...fadeUp} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 border-t border-line" data-id="element-2884" data-mp="2z9pjquu0whoz">
            {inquiryReasons.map(reason => <li key={reason.value} className="border-b border-line" data-id="element-2885" data-mp="1drymfksb8cci">
                <button type="button" onClick={() => chooseReason(reason.value)} className="group flex w-full h-full items-start gap-4 py-7 text-left" data-id="element-2886" data-mp="1dub9udgu7ewy">
                  <span className="flex-1" data-id="element-2887" data-mp="1rsri48ucldh7">
                    <span className="block font-heading font-bold text-lg text-ink-dark group-hover:text-navy transition-colors duration-150" data-id="element-2888" data-mp="1rrq3xvi1cav9">
                      {reason.title}
                    </span>
                    <span className="mt-1.5 block text-ink-muted leading-relaxed" data-id="element-2889" data-mp="21iuiiu3sttpk">{reason.description}</span>
                  </span>
                  <ArrowRight size={18} aria-hidden="true" className="mt-1 shrink-0 text-teal transition-transform duration-150 ease-out group-hover:translate-x-1" data-id="element-2890" data-mp="2aizgkdn3rfr2" />
                </button>
              </li>)}
          </motion.ul>
        </div>
      </section>
    </>;
}